import { Input } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ImageIcon from "components/icons/ImageIcon";
import useBusiness from "graphql/useBusiness";
import { useCallback, useState } from "react";
import { useFormik } from "formik";
import PhoneInput from "react-phone-number-input";
import es from "react-phone-number-input/locale/es";
import useBusinessMutations from "graphql/useBusinessMutations";
import Loading from "components/common/loading";
import { useMessaging } from "context/MessagingContext";
import { useTranslation } from "react-i18next";
import s from "../../styles/Settings.styles";

function GeneralInfo() {
  const { t } = useTranslation();

  const { business, loading } = useBusiness();

  const [editing, setediting] = useState(false);
  const [saving, setSaving] = useState(false);

  const { setInfoMessage, setErrorMessage } = useMessaging();

  const { updateBusiness } = useBusinessMutations();
  const {
    getFieldProps,
    handleSubmit,
    resetForm,
    dirty,
    isValid,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: business?.name || "",
      idNumber: business?.idNumber || "",
      address1: business?.address1 || "",
      address2: business?.address2 || "",
      city: business?.city || "",
      country: business?.country || "",
      image: business?.image || "",
      color: business?.color || "",
      language: business?.language || "",
      phone: business?.phone || "",
      phone2: business?.phone2 || "",
      webPage: business?.webPage || "",
      zip: business?.zip || "",
      currencyCode: business?.currencyCode || "",
      decimalPlaces: business?.decimalPlaces || "",
    },

    onSubmit: async (values) => {
      try {
        setediting(true);
        setSaving(true);
        await updateBusiness(business, values);
        setSaving(false);
        setediting(false);
        onUpdateSuccess();
      } catch (error) {
        setediting(false);
        setErrorMessage({ error });
      }
    },
  });

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.SettingsGeneralInfo.infoEdit"),
      info1: t("containers.SettingsGeneralInfo.infoEditDB"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading />;

  return (
    <>
      <s.TopSectionsContainer>
        <s.LeftContainer>
          <s.SectionTitle style={{ marginBottom: "18px" }}>
            {t("containers.SettingsGeneralInfo.informationGeneral")}
          </s.SectionTitle>

          <s.Line style={{ marginBottom: "10px" }} />

          <s.gridInputContainer>
            <s.InputContainer>
              <s.SubTitle>
                {t("containers.SettingsGeneralInfo.businessName")}
              </s.SubTitle>
              <Input
                placeholder={t("general.textPlaceholder")}
                disabled={!editing}
                value={business?.name}
                {...getFieldProps("name")}
              />
            </s.InputContainer>

            <s.InputContainer>
              <s.SubTitle>{t("containers.SettingsGeneralInfo.NIF")}</s.SubTitle>
              <Input
                placeholder={t("general.textPlaceholder")}
                disabled={!editing}
                value={business?.idNumber}
                {...getFieldProps("idNumber")}
              />
            </s.InputContainer>
            <s.InputContainer
              style={{
                marginTop: "27px",
              }}
            >
              <s.SubTitle>
                {t("containers.SettingsGeneralInfo.mainPhone")}
              </s.SubTitle>
              <PhoneInput
                style={{
                  border: "1px solid #d9d9d9",
                  height: "100%",
                }}
                labels={es}
                limitMaxLength={true}
                international={false}
                placeholder={t("Customers.fields.phone.placeholder")}
                defaultCountry="ES"
                disabled={!editing}
                value={business.phone}
                onChange={(value) => [
                  setFieldValue("phone", value),
                  setFieldTouched("phone", value),
                ]}
              />
              {/* <Input
                placeholder={t("general.textPlaceholder")}
                disabled={!editing}
                value={business?.phone}
                {...getFieldProps("phone")}
              /> */}
            </s.InputContainer>
            <s.InputContainer
              style={{
                marginTop: "27px",
              }}
            >
              <s.SubTitle>
                {t("containers.SettingsGeneralInfo.secondaryPhone")}
              </s.SubTitle>
              <PhoneInput
                style={{
                  border: "1px solid #d9d9d9",
                  height: "100%",
                }}
                labels={es}
                limitMaxLength={true}
                international={false}
                placeholder={t("Customers.fields.phone.placeholder")}
                defaultCountry="ES"
                disabled={!editing}
                value={business.phone2}
                onChange={(value) => [
                  setFieldValue("phone2", value),
                  setFieldTouched("phone2", value),
                ]}
              />
            </s.InputContainer>
          </s.gridInputContainer>

          <s.SectionTitle style={{ marginBottom: "18px", marginTop: "68px" }}>
            {t("containers.SettingsGeneralInfo.companyAddress")}
          </s.SectionTitle>
          <s.Line style={{ marginBottom: "10px" }} />

          <s.gridInputContainer>
            <s.InputContainer>
              <s.SubTitle>
                {t("containers.SettingsGeneralInfo.streetAndNumber")}
              </s.SubTitle>
              <Input
                placeholder={t("general.textPlaceholder")}
                disabled={!editing}
                value={business?.address1}
                style={{
                  marginBottom: "27px",
                }}
                {...getFieldProps("address1")}
              />
            </s.InputContainer>

            <s.InputContainer>
              <s.SubTitle>
                {t("containers.SettingsGeneralInfo.city")}
              </s.SubTitle>
              <Input
                placeholder={t("general.textPlaceholder")}
                disabled={!editing}
                value={business?.city}
                {...getFieldProps("city")}
              />
            </s.InputContainer>
            <s.InputContainer>
              <s.SubTitle>
                {t("containers.SettingsGeneralInfo.postalCode")}
              </s.SubTitle>
              <Input
                placeholder={t("general.textPlaceholder")}
                disabled={!editing}
                value={business?.address2}
                {...getFieldProps("address2")}
              />
            </s.InputContainer>
            <s.InputContainer>
              <s.SubTitle>
                {t("containers.SettingsGeneralInfo.country")}
              </s.SubTitle>
              <Input
                placeholder={t("general.textPlaceholder")}
                disabled={!editing}
                value={business?.country}
                {...getFieldProps("country")}
              />
            </s.InputContainer>
          </s.gridInputContainer>
          <s.InputContainer>
            <s.SubTitle>
              {t("containers.SettingsGeneralInfo.website")}
            </s.SubTitle>
            <Input
              placeholder={t("general.textPlaceholder")}
              disabled={!editing}
              value={business?.webPage}
              {...getFieldProps("webPage")}
            />
          </s.InputContainer>
        </s.LeftContainer>

        {/* Start right content */}
        <s.RightContainer>
          <s.SectionTitle style={{ marginBottom: "18px" }}>
            {t("containers.SettingsGeneralInfo.companyLogo")}
          </s.SectionTitle>
          <s.PhotoSectionMiddle>
            <s.ImageContainer>
              <ImageIcon />
            </s.ImageContainer>
            <s.TextImageContainer>
              <div style={{ marginBottom: "16px" }}>
                {t("containers.SettingsGeneralInfo.addImage")}
              </div>
              <div>{t("containers.SettingsGeneralInfo.deleteImage")}</div>
            </s.TextImageContainer>
          </s.PhotoSectionMiddle>
          <s.LittleMessage style={{ marginTop: "13px", marginBottom: "18px" }}>
            {t("containers.SettingsGeneralInfo.imageLegend")}
          </s.LittleMessage>
        </s.RightContainer>
      </s.TopSectionsContainer>

      {!editing ? (
        <s.InputContainer>
          <s.button onClick={() => setediting(true)}>
            {t("containers.SettingsGeneralInfo.edit")}
          </s.button>
        </s.InputContainer>
      ) : (
        <s.containerButton>
          <s.InputContainer style={{ marginRight: "15px" }}>
            <s.button
              onClick={() => {
                setediting(false), resetForm();
              }}
            >
              {t("containers.SettingsGeneralInfo.return")}
            </s.button>
          </s.InputContainer>
          <s.InputContainer>
            <s.buttonsave
              disabled={dirty && isValid}
              onClick={() => handleSubmit()}
            >
              {t("containers.SettingsGeneralInfo.save")}
              {saving && (
                <LoadingOutlined
                  style={{
                    fontSize: 24,
                    marginLeft: "15px",
                  }}
                  spin
                />
              )}
            </s.buttonsave>
          </s.InputContainer>
        </s.containerButton>
      )}
    </>
  );
}

export default GeneralInfo;
